import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';

interface ResolvedPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  loading: boolean;
  error: string | null;
  overlaySummary: React.ReactNode;
  replaceSummary?: React.ReactNode;
}

export function ResolvedPreviewDialog({
  open,
  onClose,
  title,
  loading,
  error,
  overlaySummary,
  replaceSummary,
}: ResolvedPreviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="2xl" className="max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="min-h-0 flex-1 overflow-y-auto">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          )}
          {!loading && error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          {!loading && !error && (
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-semibold text-gray-800 dark:text-gray-200 mb-1">Effective (overlay chain)</p>
                <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/80 p-3 max-h-64 overflow-y-auto font-mono text-xs whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                  {overlaySummary}
                </div>
              </div>
              {replaceSummary != null && (
                <div>
                  <p className="font-semibold text-gray-800 dark:text-gray-200 mb-1">If entity used replace mode for this slice</p>
                  <div className="rounded-md border border-amber-200 dark:border-amber-900/50 bg-amber-50/80 dark:bg-amber-950/30 p-3 max-h-64 overflow-y-auto font-mono text-xs whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                    {replaceSummary}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function pickToolRef(tools: unknown, toolId: string): Record<string, unknown> | null {
  if (!Array.isArray(tools)) return null;
  const t = tools.find((x: any) => x?.toolId === toolId) as Record<string, unknown> | undefined;
  return t ?? null;
}

export function formatToolResolved(t: Record<string, unknown> | null): string {
  if (!t) {
    return '(No merged tool row for this id — inherits from stream/parent layers only.)';
  }
  const sourceSummary = formatToolSources(
    String(t.toolId || ''),
    (t.config as Record<string, unknown> | undefined) || undefined,
  );
  const details = JSON.stringify(
    {
      toolId: t.toolId,
      enabled: t.enabled,
      mode: t.mode,
      capabilities: t.capabilities ?? [],
      config: t.config ?? {},
    },
    null,
    2,
  );
  if (!sourceSummary) {
    return details;
  }
  return `${sourceSummary}\n${details}`;
}

function normalizeSourceValues(value: unknown): string[] {
  const flattened: string[] = [];
  const visit = (next: unknown) => {
    if (next == null) return;
    if (Array.isArray(next)) {
      for (const item of next) visit(item);
      return;
    }
    flattened.push(
      ...String(next)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean),
    );
  };
  visit(value);
  const deduped: string[] = [];
  const seen = new Set<string>();
  for (const item of flattened) {
    const key = item.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(item);
  }
  return deduped;
}

function normalizeRepoValues(values: string[]): string[] {
  const deduped: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const repo = value
      .replace(/^https?:\/\//i, '')
      .replace(/\.git$/i, '')
      .trim();
    if (!repo) continue;
    const key = repo.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(repo);
  }
  return deduped;
}

export function formatToolSources(toolId: string, config?: Record<string, unknown>): string {
  if (!config) return '';
  const pick = (...keys: string[]) => keys.flatMap((key) => normalizeSourceValues(config[key]));
  if (toolId === 'github') {
    const repos = normalizeRepoValues(pick('repos', 'repositories'));
    if (repos.length > 0) return `Sources: ${repos.join(', ')}`;
    return '';
  }
  if (toolId === 'jira') {
    const projects = normalizeSourceValues(pick('projectKey', 'projectKeys', 'project', 'projects'));
    if (projects.length > 0) return `Projects: ${projects.join(', ')}`;
    return '';
  }
  if (toolId === 'ms_graph_ado' || toolId === 'ado' || toolId === 'azure_devops') {
    const projects = normalizeSourceValues(pick('project', 'projects', 'adoProject', 'adoProjects'));
    if (projects.length > 0) return `Projects: ${projects.join(', ')}`;
    return '';
  }
  if (toolId === 'openshift') {
    const clusters = normalizeSourceValues(pick('clusters', 'cluster'));
    if (clusters.length > 0) return `Clusters: ${clusters.join(', ')}`;
    return '';
  }
  return '';
}

export function pickWorkflowType(taxonomy: unknown, wtId: string): Record<string, unknown> | null {
  if (!taxonomy || typeof taxonomy !== 'object') return null;
  const wts = (taxonomy as { workflowTypes?: unknown }).workflowTypes;
  if (!Array.isArray(wts)) return null;
  const w = wts.find((x: any) => x?.id === wtId) as Record<string, unknown> | undefined;
  return w ?? null;
}

export function formatEntityPromptValue(wt: Record<string, unknown> | null, field: string): string {
  if (!wt) return '(Workflow type not present in merged taxonomy.)';
  const ep = wt.entityPrompts as Record<string, unknown> | undefined;
  const v = ep?.[field];
  if (v != null) {
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && 'text' in v) {
      return String((v as { text?: string }).text ?? '');
    }
    return JSON.stringify(v, null, 2);
  }
  const legacy = (wt.prompts as Record<string, unknown> | undefined)?.[field];
  if (typeof legacy === 'string' && legacy) return legacy;
  return '(empty)';
}

export function formatGlobalPromptValue(prompts: unknown, field: string): string {
  if (!prompts || typeof prompts !== 'object') return '(empty)';
  const p = (prompts as Record<string, unknown>)[field];
  if (p == null) return '(empty)';
  if (typeof p === 'string') return p;
  if (typeof p === 'object' && p !== null && 'text' in p) {
    return String((p as { text?: string }).text ?? '');
  }
  return JSON.stringify(p, null, 2);
}
